import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { fireAudit } from '../utils/auditClient'; 

const BrokenRacket = () => {
  const today = new Date().toISOString().split('T')[0];
  const { selectedCenter, userData } = useUser();
  const userId = userData?.userId;
  const token = localStorage.getItem('auth-token');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    date: today,
    academy: null,
    venueQr: '',
    cash: '',
    razorpay: '',
  });

  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportsOptions, setSportsOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false); 

  const onlyDigits = (v) => (v === '' ? '' : /^\d+$/.test(v) ? v : null);
  const toInt = (v) => (v === '' ? 0 : parseInt(v, 10) || 0);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const buildSubmitPayload = useCallback(() => {
    const entries = sportsOptions.map((sport) => {
      const sportId = sport.value;
      const cash = toInt(String(formData[`cash_${sportId}`] ?? ''));
      const venueQr = toInt(String(formData[`venueQr_${sportId}`] ?? ''));
      const razorpay = toInt(String(formData[`razorpay_${sportId}`] ?? ''));
      const comments = formData[`comments_${sportId}`] || '';
      const amount = cash + venueQr + razorpay;

      return {
        sportId,
        sportName: sport.label,
        cash,
        venueQr,
        razorpay,
        amountCharged: amount,
        comments,
      };
    }).filter(e => e.amountCharged > 0 || (e.comments && e.comments.trim().length));

    return {
      date: formData.date,
      academyId: formData.academy?.value ?? null,
      userId,
      entries,
    };
  }, [formData, sportsOptions, userId]);

  const redactForAudit = useCallback((payload) => {
    if (!payload) return payload;
    const copy = JSON.parse(JSON.stringify(payload));
    if (Array.isArray(copy.entries)) {
      copy.entries.forEach(e => { if (e?.comments) e.comments = '********'; });
    }
    return copy;
  }, []);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    if (!optionsLoaded) return; 
    baselineRef.current = buildSubmitPayload();
    initializedBaselineRef.current = true;
  }, [optionsLoaded, buildSubmitPayload]);

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const fetchedAcademies = response?.data?.data?.academies || [];
        const filtered =
          userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)
            ? fetchedAcademies.filter(a => userData.academyIds.includes(a.id))
            : fetchedAcademies;

        setAcademies(filtered);
        setAcademyOptions(filtered.map(a => ({ label: a.name, value: a.id })));
        setOptionsLoaded(true);
      } catch (error) {
        console.error('Error fetching academies:', error);
      }
    };

    fetchAcademies();
  }, [userData?.userType, userData?.academyIds]);

  useEffect(() => {
    if (!selectedCenter || academies.length === 0) return;
    const selected = academies.find(a => a.id === Number(selectedCenter));
    if (!selected) return;

    const academyOpt = { label: selected.name, value: selected.id };
    setFormData(prev => ({ ...prev, academy: academyOpt }));

    const sports = (selected.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);
  }, [selectedCenter, academies]);

  const handleAcademyChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, academy: selectedOption }));

    const selected = academies.find(a => a.id === selectedOption?.value);
    const sports = (selected?.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);

    baselineRef.current = null;
    initializedBaselineRef.current = false;
  };


  const handleRowAmountChange = (sportId, field, raw) => {
    const v = onlyDigits(raw);
    if (v === null) return;
    setFormData(prev => ({
      ...prev,
      [`${field}_${sportId}`]: v === '' ? '' : parseInt(v, 10)
    }));
  };

  const handleRowCommentChange = (sportId, value) => {
    setFormData(prev => ({
      ...prev,
      [`comments_${sportId}`]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { date, academy } = formData;

    if (!date) {
      Swal.fire({ icon: 'error', title: 'Date Missing!', text: 'Please select a date.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!academy) {
      Swal.fire({ icon: 'error', title: 'Academy Not Selected!', text: 'Please select an academy.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (sportsOptions.length === 0) {
      Swal.fire({ icon: 'error', title: 'No Sports', text: 'This academy has no configured sports to submit against.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const payloads = [];

    sportsOptions.forEach((sport) => {
      const sportId = sport.value;
      const cash = toInt(String(formData[`cash_${sportId}`] ?? ''));
      const venueQr = toInt(String(formData[`venueQr_${sportId}`] ?? ''));
      const razorpay = toInt(String(formData[`razorpay_${sportId}`] ?? ''));
      const commentText = formData[`comments_${sportId}`] || '';
      const total = cash + venueQr + razorpay;

      if (total > 0) {
        payloads.push({
          id: null,
          academyId: academy.value,
          amountCharged: total,
          isPaid: true,
          venueQr,
          cash,
          razorpay,
          comments: commentText,
          userId,
          date: `${date}T00:00:00.000Z`,
          sportId
        });
      }
    });

    if (payloads.length === 0) {
      Swal.fire({ icon: 'error', title: 'No Payments Entered', text: 'Enter at least one payment entry for any sport.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || { date, academyId: academy.value, userId, entries: [] };

    try {
      setIsSubmitting(true);
      const results = await Promise.allSettled(
        payloads.map(p =>
          axios.post(process.env.REACT_APP_API_URL_BROKEN_RACKET, p, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            }
          })
        )
      );

      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Broken equipment entries added successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      } else if (rejected.length < results.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Partial Success',
          text: `${results.length - rejected.length} entries saved, ${rejected.length} failed.`,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3500,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'All entries failed to save.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
      const createdIds = results
        .filter(r => r.status === 'fulfilled')
        .map(r => {
          const resp = r.value;
          return resp?.data?.data?.id ?? resp?.data?.id ?? null;
        })
        .filter(Boolean);

      const affected_id = `${academy.value}-${date}`;
      fireAudit({
        entity: 'broken_equipment',
        affected_id,
        action: 'create',
        businessApi: process.env.REACT_APP_API_URL_BROKEN_RACKET,
        businessMethod: 'POST',
        oldPayload: oldPayload,
        newPayload: newPayload,
        section: 'Centre Management',
        subSection: 'Walkin Sales(Broken Equipments)',
        extra: { page: 'BrokenRacket', academyId: academy.value, date, createdIds },
      });
      baselineRef.current = newPayload;

      const resetFields = {};
      sportsOptions.forEach((sport) => {
        resetFields[`cash_${sport.value}`] = '';
        resetFields[`venueQr_${sport.value}`] = '';
        resetFields[`razorpay_${sport.value}`] = '';
        resetFields[`comments_${sport.value}`] = '';
      });
      setFormData(prev => ({ ...prev, ...resetFields }));
    } catch (error) {
      console.error('API Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to submit Broken Equipment entries.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        {/* Academy */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="academy"
            options={academyOptions}
            value={formData.academy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            menuPortalTarget={document.body}
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.academy && <label htmlFor="academy">Academy</label>}
        </div>

        {sportsOptions.length > 0 && (
          <div className="rental-entry-table-container">
            <label className="rental-entry-heading">Enter Broken Equipment Amounts</label>
            <div className="rental-table-scroll">
              <table className="rental-entry-table">
                <thead>
                  <tr>
                    <th>Sport</th>
                    <th>Cash (₹)</th>
                    <th>Venue QR (₹)</th>
                    <th>Razorpay (₹)</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {sportsOptions.map((sport) => {
                    const sid = sport.value;
                    return (
                      <tr key={sid}>
                        <td>{sport.label}</td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="rental-input"
                            placeholder="0"
                            value={
                              formData[`cash_${sid}`] === 0
                                ? '0'
                                : formData[`cash_${sid}`] ?? ''
                            }
                            onChange={(e) => handleRowAmountChange(sid, 'cash', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="rental-input"
                            placeholder="0"
                            value={
                              formData[`venueQr_${sid}`] === 0
                                ? '0'
                                : formData[`venueQr_${sid}`] ?? ''
                            }
                            onChange={(e) => handleRowAmountChange(sid, 'venueQr', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="rental-input"
                            placeholder="0"
                            value={
                              formData[`razorpay_${sid}`] === 0
                                ? '0'
                                : formData[`razorpay_${sid}`] ?? ''
                            }
                            onChange={(e) => handleRowAmountChange(sid, 'razorpay', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="rental-input rental-comment"
                            placeholder="Optional comment"
                            value={formData[`comments_${sid}`] || ''}
                            onChange={(e) => handleRowCommentChange(sid, e.target.value)}
                            maxLength={300}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <button className="submit-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default BrokenRacket;
